// ─────────────────────────────────────────────────────────────────────────────
//  notifications.js  —  Torch App · Push Notification Manager
//
//  Handles:
//  1. Permission request (tasteful — only after first settlement)
//  2. FCM token generation + storage in Firestore
//  3. Foreground message display (app is open)
//  4. Notification trigger after pick settlement
//  5. Token refresh handling
//
//  Background push (app closed) is handled by firebase-messaging-sw.js.
//  When you add Cloud Functions, pass the stored FCM tokens to
//  admin.messaging().sendMulticast() using the tokens in users/{uid}/fcmTokens.
// ─────────────────────────────────────────────────────────────────────────────

import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import {
  getFirestore, doc, setDoc, deleteDoc,
  collection, getDocs,
} from 'firebase/firestore';
import app from './firebase';

const db = getFirestore(app);
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

// ── Lazy messaging instance (not all browsers support it) ────────────────────
let _messaging = null;
async function getMsg() {
  if (_messaging) return _messaging;
  const supported = await isSupported();
  if (!supported) return null;
  _messaging = getMessaging(app);
  return _messaging;
}

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION + TOKEN
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Request notification permission if not already granted/denied.
 * Returns: 'granted' | 'denied' | 'default' | 'unsupported'
 */
export async function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied')  return 'denied';
  try {
    const result = await Notification.requestPermission();
    return result;
  } catch {
    return 'unsupported';
  }
}

/**
 * Get FCM token and save it to Firestore under users/{uid}/fcmTokens/{token}.
 * Safe to call multiple times — idempotent.
 */
export async function registerFCMToken(userId) {
  if (!userId || !VAPID_KEY) return null;
  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') return null;

    const messaging = await getMsg();
    if (!messaging) return null;

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker?.getRegistration('/'),
    });
    if (!token) return null;

    // Store token in Firestore — keyed by token so duplicates are no-ops
    await setDoc(
      doc(db, 'users', userId, 'fcmTokens', token.slice(-20)), // last 20 chars as doc ID
      { token, createdAt: new Date(), platform: navigator.platform || 'web' },
      { merge: true }
    );
    return token;
  } catch (e) {
    console.warn('[Torch] FCM token registration failed:', e.message);
    return null;
  }
}

/**
 * Remove a specific FCM token (e.g. on sign-out).
 */
export async function unregisterFCMToken(userId, token) {
  if (!userId || !token) return;
  try {
    await deleteDoc(doc(db, 'users', userId, 'fcmTokens', token.slice(-20)));
  } catch {}
}

/**
 * Get all stored FCM tokens for a user (used by backend/Cloud Functions).
 */
export async function getUserFCMTokens(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'fcmTokens'));
    return snap.docs.map(d => d.data().token).filter(Boolean);
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FOREGROUND MESSAGES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Listen for FCM messages while the app is in the foreground.
 * Returns unsubscribe function.
 */
export async function listenForForegroundMessages(onReceive) {
  const messaging = await getMsg();
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    onReceive(payload);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// IN-APP NOTIFICATION AFTER SETTLEMENT
// Called from MyPicksPage after settleUserPicks() resolves.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Show a rich in-app notification banner after settlement.
 * Also shows a browser Notification if permission is granted and app is backgrounded.
 *
 * @param {Array} settledPicks — array of settled pick objects from settleUserPicks()
 */
export function notifySettlement(settledPicks) {
  if (!settledPicks || settledPicks.length === 0) return;

  const wins   = settledPicks.filter(p => p.status === 'won');
  const losses = settledPicks.filter(p => p.status === 'lost');
  const pushes = settledPicks.filter(p => p.status === 'push');
  const netPts = settledPicks.reduce((s, p) => s + (p.points || 0), 0);

  // Build message
  let title, body, type;
  if (settledPicks.length === 1) {
    const p = settledPicks[0];
    if (p.status === 'won') {
      title = `🔥 ${p.label} — HIT!`;
      body  = `+${p.points} pts${p.leagueId ? ' in your league' : ''}`;
      type  = 'success';
    } else if (p.status === 'lost') {
      title = `${p.label} — missed`;
      body  = `-${p.wager} pts`;
      type  = 'error';
    } else {
      title = `${p.label} — Push`;
      body  = 'Wager returned';
      type  = 'info';
    }
  } else {
    const emoji = wins.length > losses.length ? '🔥' : losses.length > wins.length ? '📉' : '⚖️';
    title = `${emoji} ${settledPicks.length} picks settled`;
    body  = `${wins.length}W · ${losses.length}L${pushes.length ? ` · ${pushes.length} push` : ''} · Net: ${netPts >= 0 ? '+' : ''}${netPts} pts`;
    type  = netPts >= 0 ? 'success' : 'error';
  }

  // In-app toast (always shown)
  window.torchToast?.(`${title} ${body}`, type);

  // Browser Notification (only when app is backgrounded or in another tab)
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    document.visibilityState === 'hidden'
  ) {
    try {
      const n = new Notification(title, {
        body,
        icon: '/icons/icon-192.png',
        badge: '/icons/icon-192.png',
        tag: 'torch-settlement',
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {}
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION PROMPT STATE
// Stored in localStorage so we don't ask repeatedly
// ─────────────────────────────────────────────────────────────────────────────

export function shouldPromptForNotifications() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'default') return false;
  const dismissed = localStorage.getItem('torch_notif_dismissed');
  if (dismissed) return false;
  const count = parseInt(localStorage.getItem('torch_settlement_count') || '0', 10);
  // Only prompt after their first settlement — they've seen the app work
  return count >= 1;
}

export function markNotificationPromptDismissed() {
  localStorage.setItem('torch_notif_dismissed', '1');
}

export function incrementSettlementCount() {
  const c = parseInt(localStorage.getItem('torch_settlement_count') || '0', 10);
  localStorage.setItem('torch_settlement_count', String(c + 1));
}
